import type { CallTaskEventId } from "@repo/app-types/entities/callTaskEvent";
import type { RecordingId } from "@repo/app-types/entities/recording";
import type {
  CallOutlineSegment,
  TeammateAccountCallOutline,
} from "@repo/app-types/entities/teammateAccountCallOutline";
import type {
  AccountGradingRecording,
  CustomerInteractionTextThread,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import type { Seconds } from "@repo/app-types/units/duration";
import { DefaultSkeleton } from "@repo/ui/Skeletons/skeletons";
import {
  IconChevronDown,
  IconChevronUp,
  IconMessage,
  IconPhone,
} from "@tabler/icons-react";
import { DateTime, Duration } from "luxon";
import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from "react";
import {
  Button as AriaButton,
  type ButtonProps as AriaButtonProps,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { renderCallLength } from "./durationFormatting.js";
import {
  buildTranscriptNavigationItems,
  checkCallSegmentContainsPosition,
  describeTranscriptNavigationItems,
  type CallPosition,
  type TextConversationNavigationItem,
  type TranscriptPosition,
} from "./transcriptNavigationHelpers.js";

export interface TranscriptNavigatorProps {
  recordings: readonly AccountGradingRecording[];
  textThread?: CustomerInteractionTextThread;
  callOutline: TeammateAccountCallOutline | undefined;
  isOutlineLoading?: boolean;
  isOutlineError?: boolean;
  /** The recording selected in the transcript audio player. */
  expandedRecordingId: RecordingId | undefined;
  /** Shows every call's key moments without disclosure controls. */
  expandAll?: boolean;
  /** The key moment under the audio playhead. */
  audioPosition?: CallPosition;
  /** The call fragment or text on the transcript's reading line. */
  readingPosition?: TranscriptPosition;
  onCallRequest: (recordingId: RecordingId) => void;
  onSegmentRequest: (position: CallPosition) => void;
  onTextConversationRequest: (textConversationId: CallTaskEventId) => void;
}

export interface CallNavigationRowProps {
  callNumber: number;
  recordedAt: string;
  duration: Seconds;
  title: string;
  keyMoment?: CallOutlineSegment;
  /** Keeps the key-moment line's space when no moment matches the current position. */
  reserveKeyMomentSpace?: boolean;
  isExpanded?: boolean;
  isStuck?: boolean;
  /** Connects the call icon to its expanded key moments. */
  showKeyMomentRail?: boolean;
  chaptersId?: string;
  onPress?: () => void;
  className?: string;
}

export const CallNavigationRow: FC<CallNavigationRowProps> = (props) => {
  const {
    callNumber,
    recordedAt,
    duration,
    title,
    keyMoment,
    reserveKeyMomentSpace,
    isExpanded,
    isStuck,
    showKeyMomentRail,
    chaptersId,
    onPress,
    className,
  } = props;

  return (
    <NavigationRowButton
      data-call-header
      className={twMerge(
        "relative",
        showKeyMomentRail &&
          "before:pointer-events-none before:absolute before:top-5 before:bottom-0 before:left-[22px] before:w-px before:-translate-x-1/2 before:bg-border-strong before:content-['']",
        isStuck &&
          "rounded-t-none after:pointer-events-none after:absolute after:right-0 after:-bottom-3 after:left-9 after:h-3 after:bg-linear-to-b after:from-card after:to-transparent after:content-['']",
        className,
      )}
      icon={<IconPhone className="size-3.5" />}
      title={title}
      metadata={
        <>
          Call {callNumber} &middot;{" "}
          {DateTime.fromISO(recordedAt).toLocaleString(DateTime.DATETIME_MED)}
          {" · "}
          <span>{renderCallLength(duration)}</span>
        </>
      }
      isExpanded={isExpanded}
      aria-controls={isExpanded ? chaptersId : undefined}
      onPress={onPress}
    >
      {keyMoment || reserveKeyMomentSpace ? (
        <span className="mt-1 flex min-h-4 items-start gap-2 text-xs font-normal text-muted-foreground">
          {keyMoment ? (
            <>
              <span className="shrink-0 tabular-nums">
                {Duration.fromMillis(keyMoment.start_timestamp).toFormat(
                  "h:mm:ss",
                )}
              </span>
              <span>{keyMoment.title}</span>
            </>
          ) : null}
        </span>
      ) : null}
    </NavigationRowButton>
  );
};

interface NavigationRowButtonProps extends AriaButtonProps {
  icon: ReactNode;
  title: string;
  metadata: ReactNode;
  isExpanded?: boolean | undefined;
  className?: string;
  children?: ReactNode;
}

/** A timeline node beside its title, metadata, and optional disclosure. */
const NavigationRowButton: FC<NavigationRowButtonProps> = (props) => {
  const {
    icon,
    title,
    metadata,
    isExpanded,
    className,
    children,
    ...buttonProps
  } = props;
  const hasChevron = isExpanded !== undefined;
  return (
    <AriaButton
      {...buttonProps}
      className={twMerge(
        "grid w-full grid-cols-[1.5rem_minmax(0,1fr)_1rem] items-start gap-x-2.5 gap-y-0.5 rounded-lg bg-card px-2.5 py-2.5 text-left hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:outline-none",
        className,
      )}
      aria-expanded={isExpanded}
    >
      <span
        aria-hidden="true"
        className="relative col-start-1 row-span-2 row-start-1 -mt-0.5 flex size-6 items-center justify-center rounded-full bg-gray-100 text-muted-foreground"
      >
        {icon}
      </span>
      <span
        className={twMerge(
          "col-start-2 row-start-1 min-w-0 text-xs leading-5 font-semibold text-foreground",
          !hasChevron && "col-span-2",
        )}
      >
        {title}
      </span>
      {hasChevron &&
        (isExpanded ? (
          <IconChevronUp
            aria-hidden="true"
            className="col-start-3 row-start-1 mt-0.5 size-4 text-muted-foreground"
          />
        ) : (
          <IconChevronDown
            aria-hidden="true"
            className="col-start-3 row-start-1 mt-0.5 size-4 text-muted-foreground"
          />
        ))}
      <span className="col-span-2 col-start-2 row-start-2 min-w-0 text-2xs font-normal text-muted-foreground">
        {metadata}
      </span>
      {children && (
        <span className="col-span-2 col-start-2 row-start-3 min-w-0">
          {children}
        </span>
      )}
    </AriaButton>
  );
};

export interface CallKeyMomentRowProps {
  segment: CallOutlineSegment;
  isAudioActive: boolean;
  isReadingCurrent: boolean;
  onPress: () => void;
}

export const CallKeyMomentRow: FC<CallKeyMomentRowProps> = (props) => {
  const { segment, isAudioActive, isReadingCurrent, onPress } = props;

  return (
    <div className="relative pr-2.5 pl-9">
      <AriaButton
        className={twMerge(
          "flex w-full items-start justify-between gap-3 rounded-lg px-2.5 py-2 text-left text-xs hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:outline-none",
          isReadingCurrent && "bg-gray-50",
          isAudioActive && "bg-gray-100 hover:bg-gray-100",
        )}
        aria-current={isAudioActive ? "location" : undefined}
        onPress={onPress}
      >
        <span className="min-w-0 font-medium break-words">{segment.title}</span>
        <span className="shrink-0 font-normal text-muted-foreground tabular-nums">
          {Duration.fromMillis(segment.start_timestamp).toFormat("h:mm:ss")}
        </span>
      </AriaButton>
    </div>
  );
};

interface CallKeyMomentListProps {
  recordingId: RecordingId;
  segments: readonly CallOutlineSegment[];
  audioPosition: CallPosition | undefined;
  readingPosition: TranscriptPosition | undefined;
  onSegmentRequest: (position: CallPosition) => void;
}

const CallKeyMomentList: FC<CallKeyMomentListProps> = (props) => {
  const {
    recordingId,
    segments,
    audioPosition,
    readingPosition,
    onSegmentRequest,
  } = props;
  const listRef = useRef<HTMLOListElement>(null);
  const [thumb, setThumb] = useState<{ top: number; height: number }>();
  const activeIndex = segments.findIndex((segment) =>
    checkCallSegmentContainsPosition(recordingId, segment, audioPosition),
  );

  useLayoutEffect(() => {
    const list = listRef.current;
    const activeRow = list?.children.item(activeIndex);
    if (!list || !(activeRow instanceof HTMLElement)) {
      setThumb(undefined);
      return;
    }
    const measure = () =>
      setThumb({
        top: activeRow.offsetTop + 4,
        height: activeRow.offsetHeight - 8,
      });
    measure();
    // A row that rewraps resizes the list, so the list is all we need to watch.
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    return () => observer.disconnect();
  }, [activeIndex, segments]);

  return (
    <div className="relative before:pointer-events-none before:absolute before:top-0 before:bottom-1 before:left-[22px] before:w-px before:-translate-x-1/2 before:bg-border-strong before:content-['']">
      <ol ref={listRef}>
        {segments.map((segment, index) => (
          <li
            key={`${segment.title}-${segment.start_timestamp}-${segment.end_timestamp}`}
          >
            <CallKeyMomentRow
              segment={segment}
              isAudioActive={index === activeIndex}
              isReadingCurrent={checkCallSegmentContainsPosition(
                recordingId,
                segment,
                readingPosition,
              )}
              onPress={() =>
                onSegmentRequest({
                  type: "call",
                  recordingId,
                  timestamp: segment.start_timestamp,
                })
              }
            />
          </li>
        ))}
      </ol>
      {thumb && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute top-0 left-[22px] w-[3px] -translate-x-1/2 rounded-[2px] bg-indigo-400 transition-[transform,height] duration-200 ease-out motion-reduce:transition-none"
          style={{
            transform: `translateY(${thumb.top}px)`,
            height: thumb.height,
          }}
        />
      )}
    </div>
  );
};

interface TextConversationNavigationRowProps {
  conversation: TextConversationNavigationItem;
  isReadingCurrent?: boolean;
  isExpanded?: boolean;
  /** Keeps the line a call row uses for its key moment, so the dropdown trigger keeps its height. */
  reserveKeyMomentSpace?: boolean;
  onPress?: () => void;
}

export const TextConversationNavigationRow: FC<
  TextConversationNavigationRowProps
> = (props) => {
  const {
    conversation,
    isReadingCurrent,
    isExpanded,
    reserveKeyMomentSpace,
    onPress,
  } = props;
  const messageCount = conversation.textMessageIds.length;
  return (
    <NavigationRowButton
      className={isReadingCurrent ? "bg-gray-50" : undefined}
      icon={<IconMessage className="size-3.5" />}
      title="Text conversation"
      metadata={
        <>
          {DateTime.fromISO(conversation.startedAt).toLocaleString(
            DateTime.DATETIME_MED,
          )}
          {" · "}
          {messageCount} {messageCount === 1 ? "message" : "messages"}
        </>
      }
      isExpanded={isExpanded}
      onPress={onPress}
    >
      {reserveKeyMomentSpace && (
        <span aria-hidden="true" className="mt-1 block min-h-4" />
      )}
    </NavigationRowButton>
  );
};

/** Navigates an interaction's calls, chapters, and text conversations. */
export const TranscriptNavigator: FC<TranscriptNavigatorProps> = (props) => {
  const {
    recordings,
    textThread,
    callOutline,
    isOutlineLoading,
    isOutlineError,
    expandedRecordingId,
    expandAll = false,
    audioPosition,
    readingPosition,
    onCallRequest,
    onSegmentRequest,
    onTextConversationRequest,
  } = props;
  const items = useMemo(
    () => buildTranscriptNavigationItems(recordings, callOutline, textThread),
    [recordings, callOutline, textThread],
  );
  const navigationId = useId();
  const navRef = useRef<HTMLElement>(null);
  const [stuckCallId, setStuckCallId] = useState<RecordingId>();

  useEffect(() => {
    const activeCall = Array.from(
      navRef.current?.querySelectorAll<HTMLElement>(
        "[data-call-recording-id]",
      ) ?? [],
    ).find(
      (element) => element.dataset.callRecordingId === expandedRecordingId,
    );
    activeCall?.scrollIntoView({ block: "nearest" });
  }, [expandedRecordingId]);

  useEffect(() => {
    const nav = navRef.current;
    const scrollPane = nav?.closest<HTMLElement>(
      "[data-call-scroll-container]",
    );
    if (!nav || !scrollPane) {
      return;
    }

    const updateStuckCall = () => {
      const stickyTop =
        scrollPane.getBoundingClientRect().top +
        Number.parseFloat(getComputedStyle(scrollPane).paddingTop);
      const stuckSection = Array.from(
        nav.querySelectorAll<HTMLElement>("[data-call-recording-id]"),
      ).find((section) => {
        const bounds = section.getBoundingClientRect();
        const stickyHeaderHeight =
          section.querySelector<HTMLElement>("[data-call-sticky-header]")
            ?.offsetHeight ?? 0;
        return (
          bounds.top < stickyTop - 1 &&
          bounds.bottom > stickyTop + stickyHeaderHeight
        );
      });
      const nextId = recordings.find(
        (recording) => recording.id === stuckSection?.dataset.callRecordingId,
      )?.id;
      setStuckCallId((currentId) =>
        currentId === nextId ? currentId : nextId,
      );
    };

    scrollPane.addEventListener("scroll", updateStuckCall, { passive: true });
    window.addEventListener("resize", updateStuckCall);
    updateStuckCall();
    return () => {
      scrollPane.removeEventListener("scroll", updateStuckCall);
      window.removeEventListener("resize", updateStuckCall);
    };
  }, [expandAll, expandedRecordingId, items, recordings]);

  return (
    <nav ref={navRef} aria-label={describeTranscriptNavigationItems(items)}>
      <ol>
        {items.map((item) => {
          if (item.type === "textConversation") {
            const textConversationId = item.id;
            return (
              <li key={textConversationId} className="pt-2">
                <TextConversationNavigationRow
                  conversation={item}
                  isReadingCurrent={
                    readingPosition?.type === "textMessage" &&
                    item.textMessageIds.includes(readingPosition.textMessageId)
                  }
                  onPress={() => onTextConversationRequest(textConversationId)}
                />
              </li>
            );
          }
          const { id, startedAt, duration, callNumber, title, segments } = item;
          const isExpanded = expandAll || id === expandedRecordingId;
          const chaptersId = `${navigationId}-call-${id}-chapters`;

          return (
            <li key={id} data-call-recording-id={id}>
              <div
                data-call-sticky-header
                className="sticky top-0 z-10 before:block before:h-2 before:bg-white before:content-['']"
              >
                <CallNavigationRow
                  callNumber={callNumber}
                  recordedAt={startedAt}
                  duration={duration}
                  title={title}
                  isExpanded={expandAll ? undefined : isExpanded}
                  isStuck={stuckCallId === id}
                  showKeyMomentRail={isExpanded && segments.length > 0}
                  chaptersId={chaptersId}
                  onPress={() => {
                    if (expandAll || !isExpanded) {
                      onCallRequest(id);
                    }
                  }}
                />
              </div>

              {isExpanded ? (
                <div id={chaptersId}>
                  {segments.length > 0 ? (
                    <CallKeyMomentList
                      recordingId={id}
                      segments={segments}
                      audioPosition={audioPosition}
                      readingPosition={readingPosition}
                      onSegmentRequest={onSegmentRequest}
                    />
                  ) : isOutlineLoading ? (
                    <div className="px-3 py-2">
                      <DefaultSkeleton />
                    </div>
                  ) : isOutlineError ? (
                    <p className="px-3 py-2 text-sm text-destructive">
                      Failed to load call chapters.
                    </p>
                  ) : null}
                </div>
              ) : null}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
